const DEFAULT_RUNTIME_CACHE_KEY = 'runtime_server_config'
const DEFAULT_CONFIG_SERVER_KEY = 'runtime_config_server'

function normalizeBaseUrl(url = '') {
	return String(url || '').trim().replace(/\/+$/, '')
}

function normalizePath(path = '') {
	if (!path) {
		return ''
	}
	return path.startsWith('/') ? path : `/${path}`
}

function joinUrl(baseUrl = '', path = '') {
	const normalizedBaseUrl = normalizeBaseUrl(baseUrl)
	const normalizedPath = normalizePath(path)
	if (!normalizedBaseUrl) {
		return normalizedPath
	}
	return `${normalizedBaseUrl}${normalizedPath}`
}

function parseJsonStorage(key) {
	try {
		const value = localStorage.getItem(key)
		return value ? JSON.parse(value) : null
	} catch (error) {
		console.warn('[runtime-config] 读取本地缓存失败:', key, error)
		return null
	}
}

function setJsonStorage(key, value) {
	try {
		localStorage.setItem(key, JSON.stringify(value))
	} catch (error) {
		console.warn('[runtime-config] 写入本地缓存失败:', key, error)
	}
}

function getRuntimeCacheKey() {
	return process.env.VUE_APP_RUNTIME_SERVER_CACHE_KEY || DEFAULT_RUNTIME_CACHE_KEY
}

function getConfigServerStorageKey() {
	return process.env.VUE_APP_CONFIG_SERVER_STORAGE_KEY || DEFAULT_CONFIG_SERVER_KEY
}

function normalizeServerEntry(server = {}) {
	const httpUrl = normalizeBaseUrl(server.httpUrl || server.baseUrl)
	if (!httpUrl) {
		return null
	}
	return {
		name: server.name || httpUrl,
		description: server.description || '',
		httpUrl,
		priority: Number(server.priority || 0),
		weight: Number(server.weight || 0)
	}
}

function sortServerList(servers = []) {
	return servers
		.map(normalizeServerEntry)
		.filter(Boolean)
		.sort((left, right) => {
			if (left.priority !== right.priority) {
				return left.priority - right.priority
			}
			return right.weight - left.weight
		})
}

function getRuntimeCache() {
	return parseJsonStorage(getRuntimeCacheKey())
}

function setLastConfigServer(configServerUrl = '') {
	try {
		localStorage.setItem(getConfigServerStorageKey(), normalizeBaseUrl(configServerUrl))
	} catch (error) {
		console.warn('[runtime-config] 写入配置中心缓存失败:', error)
	}
}

function getLastConfigServer() {
	try {
		return normalizeBaseUrl(localStorage.getItem(getConfigServerStorageKey()) || '')
	} catch (error) {
		console.warn('[runtime-config] 读取配置中心缓存失败:', error)
		return ''
	}
}

function setRuntimeBaseUrl(baseUrl = '') {
	const normalizedBaseUrl = normalizeBaseUrl(baseUrl)
	window.__BOX_IM_RUNTIME_BASE_URL__ = normalizedBaseUrl
	return normalizedBaseUrl
}

function getRuntimeBaseUrl() {
	return normalizeBaseUrl(window.__BOX_IM_RUNTIME_BASE_URL__ || process.env.VUE_APP_BASE_API)
}

function setRuntimeServerList(servers = []) {
	window.__BOX_IM_RUNTIME_SERVER_LIST__ = sortServerList(servers)
	return window.__BOX_IM_RUNTIME_SERVER_LIST__
}

function getRuntimeServerList() {
	const currentList = Array.isArray(window.__BOX_IM_RUNTIME_SERVER_LIST__) ? window.__BOX_IM_RUNTIME_SERVER_LIST__ : []
	if (currentList.length) {
		return currentList
	}
	const cache = getRuntimeCache()
	return sortServerList(cache?.servers || [])
}

function saveRuntimeState({ configServerUrl = '', baseUrl = '', servers = [], payload = null, expireAt = 0 } = {}) {
	const normalizedServers = setRuntimeServerList(servers)
	const normalizedBaseUrl = setRuntimeBaseUrl(baseUrl)
	const now = Date.now()
	const ttl = Number(process.env.VUE_APP_RUNTIME_SERVER_CACHE_TTL || 0)
	const finalExpireAt = Number(expireAt) > now ? Number(expireAt) : now + ttl
	const cache = {
		configServerUrl: normalizeBaseUrl(configServerUrl),
		baseUrl: normalizedBaseUrl,
		servers: normalizedServers,
		payload,
		expireAt: finalExpireAt,
		updatedAt: now
	}
	setJsonStorage(getRuntimeCacheKey(), cache)
	if (cache.configServerUrl) {
		setLastConfigServer(cache.configServerUrl)
	}
	return cache
}

function loadCachedRuntimeState() {
	const cache = getRuntimeCache()
	if (!cache || !cache.baseUrl) {
		return null
	}
	return {
		...cache,
		servers: sortServerList(cache.servers || []),
		isExpired: Number(cache.expireAt || 0) <= Date.now()
	}
}

function applyCachedRuntimeState(cache) {
	if (!cache || !cache.baseUrl) {
		return null
	}
	setRuntimeServerList(cache.servers || [])
	setRuntimeBaseUrl(cache.baseUrl)
	if (cache.configServerUrl) {
		setLastConfigServer(cache.configServerUrl)
	}
	return cache.baseUrl
}

function switchToNextRuntimeServer(excludeBaseUrl = '') {
	const normalizedExcludeBaseUrl = normalizeBaseUrl(excludeBaseUrl || getRuntimeBaseUrl())
	const nextServer = getRuntimeServerList().find(server => server.httpUrl && server.httpUrl !== normalizedExcludeBaseUrl)
	if (!nextServer) {
		return ''
	}
	console.warn('[runtime-config] 切换备用服务地址:', nextServer.httpUrl)
	setRuntimeBaseUrl(nextServer.httpUrl)
	return nextServer.httpUrl
}

export {
	applyCachedRuntimeState,
	getLastConfigServer,
	getRuntimeBaseUrl,
	getRuntimeServerList,
	joinUrl,
	loadCachedRuntimeState,
	normalizeBaseUrl,
	saveRuntimeState,
	setLastConfigServer,
	setRuntimeBaseUrl,
	setRuntimeServerList,
	sortServerList,
	switchToNextRuntimeServer
}
